<script setup lang="ts">
/** Port de `.event-hero` (design-reference/styles.css), variantes mobile et bureau incluses. */
</script>

<template>
  <section class="event-hero">
    <img
      src="/assets/hero-sarah-quentin-beach.webp"
      alt="Sarah et Quentin s’embrassent sur la plage au coucher du soleil"
      fetchpriority="high"
    />
    <div class="event-hero__shade" aria-hidden="true"></div>
    <div class="event-hero__content">
      <p>Le mariage de</p>
      <!-- Prénoms empilés plutôt qu'alignés : chacun garde sa pleine hauteur au
           lieu d'être écrasé par la largeur disponible, et l'esperluette
           manuscrite devient une respiration entre les deux. -->
      <h1>Sarah <em>&amp;</em><br />Quentin</h1>
    </div>
  </section>
</template>

<style scoped>
.event-hero {
  position: relative;
  height: min(66svh, 570px);
  min-height: 430px;
  color: white;
  overflow: hidden;
}

.event-hero > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 58%;
  animation: var(--animate-hero-in);
}

/* Voile allégé : les prénoms empilés portent leur propre ombre, le dégradé
   n'a plus à assombrir la photo pour les rendre lisibles. */
.event-hero__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgb(22 30 24 / 0.18), transparent 32%),
    linear-gradient(0deg, rgb(38 43 33 / 0.5), rgb(38 43 33 / 0.2) 45%, transparent 72%);
}

.event-hero__content {
  position: absolute;
  right: 24px;
  bottom: 35px;
  left: 24px;
  text-align: center;
}

.event-hero__content h1 em {
  display: inline-block;
  margin-left: 0.06em;
  color: #f0cdbd;
  font-family: var(--font-script);
  font-size: 0.62em;
  font-style: normal;
  transform: rotate(-6deg) translateY(-0.06em);
}

.event-hero__content > p {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.23em;
  text-transform: uppercase;
  text-shadow: 0 1px 10px rgb(20 26 20 / 0.55);
}

/* `line-height: 0.8` rognait les descendantes du « Q » de Quentin, et la
   borne haute de 112px faisait déborder le titre hors du dégradé sombre :
   à contre-jour sur le sable, les lettres passaient en dessous du seuil de
   lisibilité. Taille plafonnée, interligne rendu au titre, ombre portée. */
.event-hero__content h1 {
  margin: 0;
  padding-bottom: 0.08em;
  font-family: var(--font-serif);
  font-size: clamp(44px, 13vw, 92px);
  font-weight: 400;
  line-height: 0.92;
  letter-spacing: -0.04em;
  text-shadow: 0 3px 22px rgb(20 26 20 / 0.5);
  -webkit-text-stroke: 0.25px currentColor;
}

@media (max-width: 759px) {
  /* Raccourci pour que les trois actions de partage tiennent dans le premier
     écran : sur un mobile de 667px de haut, un hero de 245px repoussait la
     troisième sous la ligne de flottaison. */
  .event-hero {
    height: min(38svh, 260px);
    min-height: 200px;
  }

  /* Le couple est décalé à droite du cadre : le titre occupe la plage et le
     ciel, plutôt que de se poser sur les visages. */
  .event-hero > img {
    object-position: 74% center;
  }

  .event-hero__shade {
    background:
      linear-gradient(90deg, rgb(43 49 38 / 0.76), rgb(43 49 38 / 0.4) 52%, transparent 82%),
      linear-gradient(0deg, rgb(43 49 38 / 0.62), transparent 72%);
  }

  .event-hero__content {
    right: auto;
    bottom: 16px;
    left: 20px;
    width: 62%;
    text-align: left;
  }

  .event-hero__content > p {
    margin-bottom: 4px;
    font-size: 11px;
  }

  .event-hero__content h1 {
    font-size: 44px;
    line-height: 0.92;
  }
}

@media (min-width: 760px) {
  .event-hero {
    margin: 18px 24px 0;
    border-radius: 24px;
  }

  /* Même parti que sur mobile : le titre occupe la plage et le ciel, le couple
     garde la droite du cadre. Centré, il se posait sur les visages. */
  .event-hero > img {
    object-position: 70% 58%;
  }

  /* Le voile devient directionnel : il assombrit la moitié gauche, où vit le
     titre, et laisse la photo intacte à droite. Un dégradé seulement vertical
     ne suffisait plus une fois le titre déplacé sur le sable clair, où du blanc
     tombait sous le seuil de lisibilité. */
  .event-hero__shade {
    background:
      linear-gradient(90deg, rgb(34 40 30 / 0.66), rgb(34 40 30 / 0.34) 42%, transparent 66%),
      linear-gradient(0deg, rgb(38 43 33 / 0.44), transparent 55%);
  }

  .event-hero__content {
    right: auto;
    /* L'accroche remonte de 48px sur le hero : sans cette réserve, elle
       recouvrait le bas du titre. */
    bottom: 104px;
    left: 56px;
    width: min(48%, 560px);
    text-align: left;
  }
}

@media (min-width: 1100px) {
  .event-hero {
    height: 600px;
  }
}
</style>
